import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import { Plus, ClipboardList, Filter, Search, ArrowRight, Calendar, Globe } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const STATUS_CONFIG = {
  draft: { label: "Draft", class: "bg-slate-100 text-slate-600" },
  data_collection: { label: "Data Collection", class: "bg-blue-50 text-blue-700" },
  analysis: { label: "Analysis", class: "bg-indigo-50 text-indigo-700" },
  review: { label: "Under Review", class: "bg-amber-50 text-amber-700" },
  approved: { label: "Approved", class: "bg-emerald-50 text-emerald-700" },
  issued: { label: "Issued", class: "bg-green-50 text-green-800" },
  archived: { label: "Archived", class: "bg-slate-50 text-slate-400" }
};

const GEOGRAPHIES = ["global", "south africa", "kenya", "nigeria", "ghana", "ethiopia", "tanzania", "rwanda", "egypt", "morocco"];
const YEARS = [2024, 2023, 2022, 2021, 2020];

export default function Assessments() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [methodologies, setMethodologies] = useState([]);
  const [form, setForm] = useState({
    title: "", type: "sme_passport", description: "", geography: "south africa",
    reporting_year: new Date().getFullYear(), methodology_id: "", scope_1: true, scope_2: true, scope_3: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAssessments();
    loadMethodologies();
  }, [currentOrg]);

  const loadAssessments = async () => {
    setLoading(true);
    try {
      const q = currentOrg ? `?org_id=${currentOrg.id}` : "";
      const res = await client.get(`/assessments${q}`);
      setAssessments(res.data);
    } catch {}
    setLoading(false);
  };

  const loadMethodologies = async () => {
    try {
      const res = await client.get("/methodologies");
      setMethodologies(res.data.filter(m => m.status === "approved_active"));
      if (res.data.length > 0) {
        const active = res.data.find(m => m.status === "approved_active");
        if (active) setForm(f => ({ ...f, methodology_id: active.id }));
      }
    } catch {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await client.post("/assessments", { ...form, org_id: currentOrg?.id, reporting_year: parseInt(form.reporting_year) });
      toast.success("Assessment created");
      setShowModal(false);
      navigate(`/assessments/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create assessment"); }
    setSaving(false);
  };

  const filtered = assessments.filter(a => {
    const matchSearch = a.title?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
          
      <PageHeader
        title="Assessments"
        subtitle="Climate impact assessments and GHG inventories."
      />
        <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] transition-colors" data-testid="create-assessment-btn">
          <Plus size={14} /> New Assessment
        </button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Total Assessments" value={assessments.length} color="text-slate-900" />
        <MetricCard label="Showing" value={filtered.length} color="text-teal-700" />
        <MetricCard label="Status" value={statusFilter === "all" ? "All Statuses" : statusFilter} color="text-slate-500" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search assessments..."
            className="w-full h-9 pl-8 pr-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]"
            data-testid="assessment-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:ring-2 focus:ring-[#059669]"
          data-testid="status-filter"
        >
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <SectionCard noPadding>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="assessments-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Assessment</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden sm:table-cell">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Year</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Geography</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <tr key={i}><td colSpan={6} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center">
                    <ClipboardList size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm text-slate-500">No assessments found</p>
                    {!search && <button onClick={() => setShowModal(true)} className="mt-3 text-xs text-[#059669] hover:underline">Create your first assessment</button>}
                  </td>
                </tr>
              ) : filtered.map(a => (
                <tr key={a.id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate(`/assessments/${a.id}`)} data-testid={`assessment-row-${a.id}`}>
                  <td className="px-5 py-4">
                    <p className="text-sm font-medium text-slate-900">{a.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5 hidden sm:block">{a.description?.substring(0, 60)}{a.description?.length > 60 ? "..." : ""}</p>
                  </td>
                  <td className="px-4 py-4 hidden sm:table-cell">
                    <span className="text-xs text-slate-500 capitalize">{a.type?.replace(/_/g, " ")}</span>
                  </td>
                  <td className="px-4 py-4 hidden md:table-cell">
                    <span className="text-sm text-slate-600 tabular-nums">{a.reporting_year || "—"}</span>
                  </td>
                  <td className="px-4 py-4 hidden lg:table-cell">
                    <span className="text-xs text-slate-500 capitalize">{a.geography || "global"}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[a.status]?.class || "bg-slate-100 text-slate-600"}`}>
                      {STATUS_CONFIG[a.status]?.label || a.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <ArrowRight size={14} className="text-slate-300" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </SectionCard>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Climate Assessment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Assessment Title *</label>
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" placeholder="e.g. FY2024 GHG Inventory" data-testid="assessment-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:ring-2 focus:ring-[#059669]">
                  <option value="sme_passport">SME Climate Passport</option>
                  <option value="project_impact">Project Impact Assessment</option>
                  <option value="corporate">Corporate Assessment</option>
                  <option value="financial">Financial Institution</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Reporting Year</label>
                <select value={form.reporting_year} onChange={e => setForm(f => ({ ...f, reporting_year: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:ring-2 focus:ring-[#059669]">
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Geography</label>
              <select value={form.geography} onChange={e => setForm(f => ({ ...f, geography: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:ring-2 focus:ring-[#059669]">
                {GEOGRAPHIES.map(g => <option key={g} value={g} className="capitalize">{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
              </select>
            </div>
            {methodologies.length > 0 && (
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Methodology</label>
                <select value={form.methodology_id} onChange={e => setForm(f => ({ ...f, methodology_id: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:ring-2 focus:ring-[#059669]">
                  {methodologies.map(m => <option key={m.id} value={m.id}>{m.name} v{m.version}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669] resize-none h-16" placeholder="Brief description of this assessment" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 h-10 border border-slate-300 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 h-10 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] disabled:opacity-60" data-testid="create-assessment-submit-btn">
                {saving ? "Creating..." : "Create Assessment"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
